import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const WarningSignsGuide = () => {
  const warningSigns = [
  {
    id: 1,
    severity: "critical",
    icon: "Flame",
    title: "Burning Smell or Smoke",
    description: "Any burning odor from outlets, switches, or electrical panel requires immediate action. This indicates overheating wires or components.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_147757b96-1767079609479.png",
    imageAlt: "Close-up of electrical outlet with visible burn marks and discoloration on white wall showing heat damage",
    action: "Turn off power immediately and call emergency services",
    urgency: "Call Now: 082 793 5785"
  },
  {
    id: 2,
    severity: "critical",
    icon: "Zap",
    title: "Sparks from Outlets",
    description: "Visible sparks when plugging in devices or from electrical panels indicate dangerous short circuits or loose connections.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17c7091a8-1767007714017.png",
    imageAlt: "Electrical sparks and arcing visible from damaged outlet with exposed wiring in residential setting",
    action: "Stop using the outlet and disconnect power",
    urgency: "Emergency: 082 793 5785"
  },
  {
    id: 3,
    severity: "high",
    icon: "Thermometer",
    title: "Hot Outlets or Switches",
    description: "Outlets or switches that are warm or hot to touch indicate overloading or faulty wiring that could lead to fire.",
    image: "https://images.unsplash.com/photo-1617032885765-956c7b53adb4",
    imageAlt: "Hand touching warm electrical outlet on wall with thermal imaging showing heat concentration around outlet",
    action: "Reduce load and schedule immediate inspection",
    urgency: "Call Today: 082 793 5785"
  },
  {
    id: 4,
    severity: "high",
    icon: "ToggleLeft",
    title: "Frequent Circuit Breaker Trips",
    description: "Circuit breakers that trip repeatedly indicate overloaded circuits or electrical faults requiring professional diagnosis.",
    image: "https://images.unsplash.com/photo-1620937963496-8aaf018ded6f",
    imageAlt: "Open electrical panel showing multiple circuit breakers with one in tripped position highlighted",
    action: "Identify the circuit and reduce electrical load",
    urgency: "Schedule: 082 793 5785"
  },
  {
    id: 5,
    severity: "medium",
    icon: "Lightbulb",
    title: "Flickering or Dimming Lights",
    description: "Lights that flicker or dim when appliances turn on suggest loose connections or inadequate electrical capacity.",
    image: "https://images.unsplash.com/photo-1680933611858-8d575c3a7524",
    imageAlt: "Ceiling light fixture with visible flickering effect and unstable illumination in modern room",
    action: "Monitor frequency and note when it occurs",
    urgency: "Consult: 082 793 5785"
  },
  {
    id: 6,
    severity: "medium",
    icon: "Volume2",
    title: "Buzzing or Humming Sounds",
    description: "Unusual sounds from outlets, switches, or electrical panel indicate loose connections or electrical arcing.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f4f952b1-1768148914973.png",
    imageAlt: "Electrical panel with sound waves illustrated showing buzzing noise emanating from circuit breaker box",
    action: "Identify source and avoid using affected circuits",
    urgency: "Book Inspection: 082 793 5785"
  }];


  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'var(--color-error)';
      case 'high':
        return 'var(--color-warning)';
      case 'medium':
        return 'var(--color-accent)';
      default:
        return 'var(--color-primary)';
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-error/10 border-error/20';
      case 'high':
        return 'bg-warning/10 border-warning/20';
      case 'medium':
        return 'bg-accent/10 border-accent/20';
      default:
        return 'bg-primary/10 border-primary/20';
    }
  };

  const handleEmergencyCall = () => {
    window.location.href = 'tel:0827935785';
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <h2 className="font-headline font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-4">
            Electrical Warning Signs Visual Guide
          </h2>
          <p className="font-body text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Learn to recognize dangerous electrical warning signs before they become emergencies. Early detection can prevent fires, injuries, and costly repairs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {warningSigns?.map((sign) =>
          <div
            key={sign?.id}
            className={`bg-card border rounded-xl overflow-hidden hover:shadow-elevated transition-all duration-300 ${getSeverityBg(sign?.severity)}`}>

              <div className="aspect-[4/3] overflow-hidden">
                <Image
                src={sign?.image}
                alt={sign?.imageAlt}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />

              </div>

              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div
                  className="flex items-center justify-center w-10 h-10 rounded-lg"
                  style={{ backgroundColor: `${getSeverityColor(sign?.severity)}15` }}>

                    <Icon name={sign?.icon} size={20} color={getSeverityColor(sign?.severity)} />
                  </div>
                  <h3 className="font-headline font-semibold text-lg text-foreground">
                    {sign?.title}
                  </h3>
                </div>

                <p className="font-body text-sm text-muted-foreground mb-4">
                  {sign?.description}
                </p>

                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertCircle" size={16} color={getSeverityColor(sign?.severity)} className="mt-0.5 flex-shrink-0" />
                    <p className="font-body text-xs text-foreground">
                      <span className="font-semibold">Action: </span>
                      {sign?.action}
                    </p>
                  </div>

                  <button
                  onClick={handleEmergencyCall}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-accent text-sm transition-colors"
                  style={{
                    backgroundColor: `${getSeverityColor(sign?.severity)}15`,
                    color: getSeverityColor(sign?.severity)
                  }}>

                    <Icon name="Phone" size={16} />
                    <span>{sign?.urgency}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 bg-card border border-border rounded-xl p-6 md:p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-headline font-bold text-xl md:text-2xl text-foreground mb-4">
                Don't Wait for an Emergency
              </h3>
              <p className="font-body text-sm md:text-base text-muted-foreground mb-6">
                Regular electrical inspections can identify potential hazards before they become dangerous. With 30+ years of experience, Gert provides comprehensive safety assessments for homes and businesses.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                "Complete electrical system inspection",
                "Certificate of Compliance (COC) issued",
                "Detailed safety report with recommendations",
                "Priority scheduling for identified hazards"]?.
                map((item, index) =>
                <li key={index} className="flex items-center gap-3 font-body text-sm text-foreground">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-success/10 flex items-center justify-center">
                      <Icon name="Check" size={14} color="var(--color-success)" />
                    </div>
                    <span>{item}</span>
                  </li>
                )}
              </ul>
              <button
                onClick={handleEmergencyCall}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-accent hover:bg-primary/90 transition-colors">

                <Icon name="Phone" size={20} />
                <span>Schedule Safety Inspection</span>
              </button>
            </div>

            <div className="relative">
              <div className="aspect-[3/4] rounded-xl overflow-hidden">
                <Image
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_175015a17-1767090394352.png"
                  alt="Professional electrician in safety gear conducting thorough electrical inspection with testing equipment in residential home"
                  className="w-full h-full object-cover" />

              </div>
              <div className="absolute -bottom-4 -left-4 bg-accent text-accent-foreground p-4 rounded-xl shadow-elevated max-w-xs">
                <div className="flex items-center gap-3">
                  <Icon name="Award" size={24} />
                  <div>
                    <div className="font-headline font-semibold text-sm">30+ Years</div>
                    <div className="font-body text-xs opacity-90">Safety Excellence</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default WarningSignsGuide;